import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { Container, Row, Column } from '@narvar/nth-grid';
import TextLink from '@narvar/nth-text-link';
import { MediumHeader } from '@narvar/nth-header';
import Loader from '../Loader';
import PickupTimeWindows from './../PickupsPage/PickupTimeWindows';
import {
  cancelForm,
  saveForm,
  setShowForm,
  updateCarrierPickupDate,
  updateCarrierPickupTimeWindow,
  updateFormField,
} from '../../actions/carrierPickup';
import AddressForm from '../AddressForm';
import CalendarDate from '../CalendarDate';
import { PICKUP } from '../../constants/returnMethods';
import { updateStep2Complete } from '../../actions/page';
import { mapAddressToDisplay } from './mapAddress';
import { formatAddressMultiLine } from '../../lib/addresses';
import { Language } from '../Language';

const mapStateToProps = ({
  language,
  page: { languageCode },
  retailerSettings: { branding: { brand_call_to_action: brandCtaColor } = {} } = {},
  returnOptions: {
    selectedOptionId,
    selectedOptionMethodKey,
    returnOptionMethods: {
      [selectedOptionMethodKey]: { get_address: addressIsEditable } = {},
    } = {},
  },
  carrierPickup: {
    isFetching,
    error,
    address: pickupAddress = {},
    pickupDates = [],
    date: pickupDate,
    form,
    showPickupDates,
    timeWindows: pickupTimeWindows,
    timeWindow: pickupTimeWindow,
  } = {},
}) => ({
  language,
  languageCode,
  selectedOptionId,
  isFetching,
  error,
  pickupAddress,
  pickupDates,
  pickupDate,
  selectedOptionMethodKey,
  brandCtaColor,
  addressIsEditable,
  form,
  showPickupDates,
  pickupTimeWindow,
  pickupTimeWindows,
});

const CarrierPickup = ({
  dispatch,
  language,
  languageCode,
  selectedOptionId,
  isFetching,
  error,
  pickupAddress,
  pickupDates,
  pickupDate,
  selectedOptionMethodKey,
  brandCtaColor,
  addressIsEditable,
  form,
  showPickupDates,
  pickupTimeWindow,
  pickupTimeWindows,
}) => {
  const { show: showForm, layout, address: formAddress, incompleteFields, cancellable } = form;
  const isPickupSelected = selectedOptionId === PICKUP;
  const isPickupDatesAvailable = pickupDates && pickupDates.length > 0;
  const formattedAddress = mapAddressToDisplay(pickupAddress, languageCode);
  const step2Complete =
    !showForm &&
    (!showPickupDates || Boolean(pickupDate)) &&
    (!pickupTimeWindows?.length || Boolean(pickupTimeWindow));

  useEffect(() => {
    if (isFetching || error || !isPickupSelected) return;
    dispatch(updateStep2Complete(step2Complete));
  }, [dispatch, error, isFetching, showPickupDates, isPickupSelected, step2Complete]);

  const AddressText = styled.p`
    padding: 0;
    margin: 0;
  `;

  if (!isPickupSelected) return null;

  if (isFetching) {
    return (
      <Container style={{ textAlign: 'center', marginTop: '1rem' }}>
        <Loader />
      </Container>
    );
  }

  if (error) {
    return (
      <Container style={{ textAlign: 'center' }}>
        <Row>
          <Column md-columns={8} md-column-offset={2}>
            <p>{language[`returns.carrier_pickup.${selectedOptionMethodKey}.error`]}</p>
          </Column>
        </Row>
      </Container>
    );
  }

  return (
    <Container style={{ textAlign: 'center', marginTop: '1rem' }} id="carrier-pickup-container">
      <Row style={{ marginBottom: '3rem' }}>
        <Column md-columns={6} md-column-offset={3}>
          <MediumHeader s={{ fontSize: '1rem', margin: '.5rem 0' }}>
            {language[`returns.carrier_pickup.${selectedOptionMethodKey}.pickup_location.title`] ||
              'Pickup Location'}
          </MediumHeader>
          {showForm ? (
            <Fragment>
              <AddressForm
                layout={layout}
                address={formAddress}
                onAddressChange={(data) => {
                  dispatch(updateFormField(data));
                }}
                incompleteFields={incompleteFields}
              />
              <Row>
                {cancellable && (
                  <TextLink
                    s={{
                      margin: '0.625rem 1rem',
                      lineHeight: '20px',
                      color: 'inherit',
                      hoverColor: 'inherit',
                      textDecoration: 'underline',
                      hoverTextDecoration: 'underline',
                    }}
                    onClick={() => {
                      dispatch(cancelForm());
                    }}
                  >
                    {language[
                      `returns.carrier_pickup.${selectedOptionMethodKey}.pickup_location.button_cancel`
                    ] || 'Cancel'}
                  </TextLink>
                )}
                <Language
                  className="carrier-pickup-info-message"
                  langKey={`returns.carrier_pickup.${selectedOptionMethodKey}.pickup_location.info_message`}
                />
                <TextLink
                  s={{
                    margin: '0.625rem 1rem',
                    lineHeight: '20px',
                    color: brandCtaColor,
                    hoverColor: brandCtaColor,
                    textDecoration: 'none',
                    hoverTextDecoration: 'none',
                    fontWeight: 700,
                  }}
                  onClick={() => {
                    dispatch(saveForm());
                  }}
                >
                  {language[
                    `returns.carrier_pickup.${selectedOptionMethodKey}.pickup_location.button_save`
                  ] || 'Save'}
                </TextLink>
              </Row>
            </Fragment>
          ) : (
            <Fragment>
              <address>
                {formatAddressMultiLine({
                  countryCode: pickupAddress.country,
                  address: {
                    ...formattedAddress,
                    name:
                      layout &&
                      layout
                        .filter((field) => field.key === 'first_name' || field.key === 'last_name')
                        .map((field) => formAddress[field.key])
                        .join(' '),
                  },
                }).map((text) => (
                  <AddressText key={Math.random()}>{text}</AddressText>
                ))}
              </address>
              {addressIsEditable && (
                <TextLink
                  s={{
                    margin: '0',
                    color: brandCtaColor,
                    hoverColor: brandCtaColor,
                    textDecoration: 'underline',
                  }}
                  onClick={() => {
                    dispatch(setShowForm(true));
                  }}
                >
                  {language[
                    `returns.carrier_pickup.${selectedOptionMethodKey}.pickup_location.button_edit`
                  ] || 'Edit'}
                </TextLink>
              )}
            </Fragment>
          )}
        </Column>
      </Row>

      {showPickupDates && (
        <Row
          style={{
            marginBottom: '1.5rem',
            ...(showForm
              ? {
                  opacity: 0.2,
                  cursor: 'not-allowed',
                }
              : { opacity: 1 }),
          }}
        >
          <Column md-columns={10} md-column-offset={1}>
            <MediumHeader s={{ fontSize: '1rem', margin: '.5rem 0' }}>
              {language[`returns.carrier_pickup.${selectedOptionMethodKey}.pickup_date.title`] ||
                'Pickup Date'}
            </MediumHeader>
            <div style={{ margin: '1rem auto' }}>
              {
                language[
                  `returns.carrier_pickup.${selectedOptionMethodKey}.pickup_date.description`
                ]
              }
            </div>
            {isPickupDatesAvailable ? (
              <>
                <CalendarDate
                  ctaColor={brandCtaColor}
                  dates={pickupDates.map(
                    ({ pickup_date: date, closed_all_day: closed, time_zone: timeZone }) => ({
                      date,
                      timeZone,
                      eligible: !closed,
                    }),
                  )}
                  onDateChange={(date, timeZone) => {
                    if (showForm) return;
                    dispatch(updateCarrierPickupDate(date, timeZone, pickupDates));
                  }}
                  selectedDate={pickupDate}
                />
                {pickupTimeWindows?.length > 0 && (
                  <PickupTimeWindows
                    timeWindows={pickupTimeWindows}
                    onClick={(time) => {
                      if (showForm) return;
                      dispatch(updateCarrierPickupTimeWindow(time));
                    }}
                    selectedPickupTime={pickupTimeWindow}
                  />
                )}
              </>
            ) : (
              <div style={{ margin: '1rem auto' }}>
                {language[`returns.carrier_pickup.${selectedOptionMethodKey}.pickup_date.no_dates`]}
              </div>
            )}
          </Column>
        </Row>
      )}
    </Container>
  );
};

CarrierPickup.defaultProps = {
  pickupDate: null,
  layout: undefined,
  pickupTimeWindows: [],
};

CarrierPickup.propTypes = {
  dispatch: PropTypes.func.isRequired,
  selectedOptionId: PropTypes.string.isRequired,
  isFetching: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired,
  selectedOptionMethodKey: PropTypes.string.isRequired,
  language: PropTypes.object.isRequired,
  languageCode: PropTypes.string.isRequired,
  countryCode: PropTypes.string.isRequired,
  pickupAddress: PropTypes.object.isRequired,
  pickupDates: PropTypes.array.isRequired,
  pickupDate: PropTypes.string,
  brandCtaColor: PropTypes.string.isRequired,
  addressIsEditable: PropTypes.bool.isRequired,
  showPickupDates: PropTypes.bool.isRequired,
  pickupTimeWindow: PropTypes.string.isRequired,
  pickupTimeWindows: PropTypes.array,
  layout: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      placeholder: PropTypes.string.isRequired,
      required: PropTypes.bool,
      minLength: PropTypes.number,
      maxLength: PropTypes.number,
      width: PropTypes.number,
    }),
  ),
  form: PropTypes.shape({
    show: PropTypes.bool,
    layout: PropTypes.object,
    address: PropTypes.object,
  }).isRequired,
};

export default connect(mapStateToProps)(CarrierPickup);
